import { useState, useEffect, useRef, useCallback } from "react";
import { useInViewAutoplay } from "@/hooks/use-inview-autoplay";
import { AnimatePresence, motion } from "framer-motion";

const prefixes = ["By", "For"];

const cards = [
  {
    label: "THE STRUGGLE",
    title: "The Struggle is Real",
    description:
      "Architects and designers work harder than most people ever see. long hours, tight deadlines, countless tools and often little reward. AI was supposed to make life easier…",
    tags: ["Burnout", "Deadlines", "Overwhelm", "Low Pay", "Long Hours"],
    hoverGradient: "linear-gradient(145deg, hsla(30, 80%, 60%, 0.18) 0%, hsla(20, 85%, 50%, 0.22) 50%, hsla(10, 75%, 45%, 0.15) 100%)",
    glowColor: "hsla(30, 85%, 55%, 0.35)",
    video: "/videos/ai-icon.mp4",
    bgColor: "white",
  },
  {
    label: "YOUR TIME",
    title: "Your Time Matters",
    description:
      "You deserve tools that are simple. Training that inspires. Solutions that save time and let you focus on design, the part that matters.",
    tags: ["Simplicity", "Focus", "Time-Saving", "Clarity", "Design First"],
    hoverGradient: "linear-gradient(145deg, hsla(0, 42%, 42%, 0.18) 0%, hsla(350, 48%, 34%, 0.22) 50%, hsla(0, 38%, 28%, 0.15) 100%)",
    glowColor: "hsla(350, 50%, 40%, 0.35)",
    video: "/videos/middle-video.mp4",
    invertVideo: true,
    bgColor: "white",
  },
  {
    label: "BY ARCHITECTS",
    title: "Built by Architects Like You",
    description:
      "We created AI Hub in two parts; Academy and Studio — built to help AI become clear, useful, and designed for the way architects really work.",
    tags: ["Academy", "Studio", "for architects", "for interior designers", "Practical"],
    hoverGradient: "linear-gradient(145deg, hsla(260, 40%, 50%, 0.15) 0%, hsla(220, 16%, 14%, 0.2) 50%, hsla(260, 30%, 40%, 0.12) 100%)",
    glowColor: "hsla(260, 40%, 60%, 0.3)",
    video: "/videos/architect-icon1.mp4",
    videoSize: "56%",
    bgColor: "white",
  },
];

const FloatingTags = ({ tags, mousePos, isHovered, isDark }: { tags: string[]; mousePos: { x: number; y: number }; isHovered: boolean; isDark?: boolean }) => {
  const positions = [
    { top: "8%", left: "6%", parallax: 0.8, floatDelay: 0 },
    { top: "4%", left: "40%", parallax: 1.2, floatDelay: 0.5 },
    { top: "8%", right: "6%", parallax: 0.6, floatDelay: 1.0 },
    { bottom: "8%", left: "5%", parallax: 1.0, floatDelay: 1.5 },
    { bottom: "4%", right: "8%", parallax: 0.9, floatDelay: 2.0 },
  ];

  return (
    <>
      {tags.map((tag, i) => {
        const pos = positions[i];
        const offsetX = mousePos.x * pos.parallax * 8;
        const offsetY = mousePos.y * pos.parallax * 6;
        const { floatDelay, parallax, ...cssPos } = pos;
        return (
          <motion.div
            key={tag}
            className="absolute z-[4] px-3.5 py-1.5 rounded-full text-[10px] font-medium tracking-wider cursor-pointer"
            style={{
              ...cssPos,
              color: isDark
                ? (isHovered ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.5)")
                : (isHovered ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.5)"),
              background: isDark
                ? (isHovered ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)")
                : (isHovered ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.05)"),
              border: isDark
                ? (isHovered ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(255,255,255,0.1)")
                : (isHovered ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(0,0,0,0.08)"),
              backdropFilter: "blur(12px)",
              boxShadow: isHovered
                ? "0 4px 16px rgba(0,0,0,0.15), 0 0 12px rgba(255,255,255,0.05)"
                : "0 2px 8px rgba(0,0,0,0.04)",
            }}
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            animate={{
              x: [0, Math.sin(floatDelay * 2) * 6, 0, Math.cos(floatDelay * 3) * -5, 0],
              y: [0, -5 - floatDelay, 3, -2 + floatDelay, 0],
            }}
            transition={{
              x: { duration: 4 + floatDelay * 0.5, repeat: Infinity, ease: "easeInOut" },
              y: { duration: 3 + floatDelay * 0.7, repeat: Infinity, ease: "easeInOut" },
            }}
            whileHover={{
              scale: 1.12,
              x: [0, 8, -6, 4, 0],
              y: [0, -6, 4, -3, 0],
              transition: {
                scale: { duration: 0.2 },
                x: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                y: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
              },
            }}
          >
            {tag}
          </motion.div>
        );
      })}
    </>
  );
};

const InViewVideo = ({ src, invertVideo, videoSize }: { src: string; invertVideo?: boolean; videoSize?: string }) => {
  const videoRef = useInViewAutoplay(0.3);
  return (
    <video
      ref={videoRef}
      src={src}
      loop
      muted
      playsInline
      preload="auto"
      className="absolute object-contain m-auto"
      style={{
        width: videoSize || "80%",
        height: videoSize || "80%",
        inset: videoSize ? "17.5%" : "10%",
        ...(invertVideo ? { filter: "invert(1)" } : {}),
      }}
    />
  );
};

export const ModernFeatureCards = () => {
  const [index, setIndex] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [mousePositions, setMousePositions] = useState<Record<number, { x: number; y: number }>>({});
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % prefixes.length);
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = (e: React.MouseEvent, cardIndex: number) => {
    const el = cardRefs.current[cardIndex];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setMousePositions((prev) => ({ ...prev, [cardIndex]: { x, y } }));
  };

  const handleMouseLeave = (cardIndex: number) => {
    setHoveredCard(null);
    setMousePositions((prev) => ({ ...prev, [cardIndex]: { x: 0, y: 0 } }));
  };

  return (
    <section className="py-24 sm:py-32 bg-background" data-nav-theme="dark">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <p className="text-base sm:text-lg uppercase tracking-[0.2em] text-muted-foreground font-general-sans mb-6">
            Why <span className="text-[hsl(0,40%,30%)]">AI Hub</span>?
          </p>
          <h2 className="text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-general-sans font-normal leading-[1.1] tracking-tight text-foreground flex flex-wrap items-center justify-center gap-x-[0.22em]">
            <span
              className="relative inline-flex h-[1.35em] w-[2.35ch] items-center justify-center overflow-hidden"
              style={{ perspective: "600px" }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={prefixes[index]}
                  initial={{ y: "90%", x: "12%", rotateX: -70, scale: 0.92, opacity: 0 }}
                  animate={{ y: "0%", x: "0%", rotateX: 0, scale: 1, opacity: 1 }}
                  exit={{ y: "-90%", x: "-12%", rotateX: 70, scale: 0.92, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
                  className="absolute inset-0 inline-flex items-center justify-center font-serif italic font-light leading-[1.1]"
                  style={{ transformOrigin: "50% 55%" }}
                >
                  {prefixes[index]}
                </motion.span>
              </AnimatePresence>
            </span>
            <span>Architects &amp; Designers.</span>
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((card, i) => {
            const mousePos = mousePositions[i] || { x: 0, y: 0 };
            const isHovered = hoveredCard === i;
            return (
              <motion.div
                key={i}
                ref={(el) => { cardRefs.current[i] = el; }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative flex flex-col rounded-3xl overflow-hidden cursor-pointer"
                style={{
                  background: card.bgColor || "white",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
                }}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseMove={(e) => handleMouseMove(e, i)}
                onMouseLeave={() => handleMouseLeave(i)}
              >
                {/* Video background */}
                <div className="relative h-[280px] sm:h-[300px] flex items-center justify-center overflow-hidden">
                  <InViewVideo
                    src={card.video}
                    invertVideo={(card as any).invertVideo}
                    videoSize={(card as any).videoSize}
                  />

                  {/* Floating tags with parallax */}
                  <FloatingTags tags={card.tags} mousePos={mousePos} isHovered={isHovered} isDark={(card as any).darkCard} />
                </div>

                {/* Color overlay — only on hover */}
                <div
                  className="absolute inset-0 rounded-3xl pointer-events-none z-[5] transition-opacity duration-500 ease-out will-change-[opacity]"
                  style={{ background: card.hoverGradient, opacity: isHovered ? 1 : 0 }}
                />

                {/* Glow shine on hover */}
                <div
                  className="absolute inset-0 rounded-3xl pointer-events-none z-[5] transition-opacity duration-400 will-change-[opacity]"
                  style={{
                    background: `radial-gradient(ellipse 80% 50% at ${50 + mousePos.x * 20}% ${40 + mousePos.y * 15}%, ${card.glowColor} 0%, transparent 70%)`,
                    opacity: isHovered ? 1 : 0,
                  }}
                />

                {/* Top glass shine */}
                <div
                  className="absolute top-0 left-0 right-0 h-[40%] rounded-t-3xl pointer-events-none z-[6] transition-opacity duration-500 will-change-[opacity]"
                  style={{
                    background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 100%)",
                    opacity: isHovered ? 1 : 0,
                  }}
                />

                {/* Bottom gradient for text readability on hover */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-[55%] z-[6] pointer-events-none transition-opacity duration-500 will-change-[opacity]"
                  style={{
                    background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)",
                    opacity: isHovered ? 1 : 0,
                  }}
                />

                {/* Text content */}
                <div className="relative z-10 px-7 pb-7">
                  <motion.span
                    className="text-[10px] uppercase tracking-[0.25em] font-medium font-general-sans mb-2.5 block"
                    animate={{ color: (card as any).darkCard ? "rgba(255,255,255,0.4)" : (isHovered ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)") }}
                    transition={{ duration: 0.4 }}
                  >
                    {card.label}
                  </motion.span>
                  <motion.h3
                    className="text-lg sm:text-xl font-semibold font-general-sans leading-snug mb-2.5"
                    animate={{ color: (card as any).darkCard ? "rgba(255,255,255,1)" : (isHovered ? "rgba(255,255,255,1)" : "rgba(0,0,0,0.85)") }}
                    transition={{ duration: 0.4 }}
                  >
                    {card.title}
                  </motion.h3>
                  <motion.p
                    className="text-[13px] leading-relaxed font-general-sans"
                    animate={{ color: (card as any).darkCard ? "rgba(255,255,255,0.5)" : (isHovered ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.45)") }}
                    transition={{ duration: 0.4 }}
                  >
                    {card.description}
                  </motion.p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
